import { ImagePaths } from '../../lib/imageUrls.js';

export const promptEngineeringBasics = {

    id: 58,
    slug: 'prompt-engineering-basics',
    ogImage: '/og/courses/prompt-engineering-basics.jpg',
    courseCode: 'AI-PROMPT-000',
    title: 'Prompt Engineering Basics: Get the Best Out of Your AI Tools',
    subtitle: 'Two evening hours that turn vague AI answers into usable work',
    valueProposition: 'Learn the handful of prompting habits that separate a disappointing AI answer from a genuinely useful one, in a single two-hour evening session with no technical background required.',
    duration: '2 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_1,
    shortDescription: 'AI-PROMPT-000 — Practical prompt writing covering prompt structure, context and role setting, and safe input for non-technical Australian professionals.',
    price: {
      aud: 195,
      currency: 'AUD'
    },
    stripeConfig: {
    priceId: 'price_1U3WMsKu1nyc322UWn3jwN1C',
    productId: 'prod_V3deEMfP9tF9ht'
  },

    // Drives the cohort registration panel on the course page. No `cohort`
    // is set until the next run is dated, so the form and the confirmation
    // email say "the next cohort".
    // Presence of this field is what renders the form — it is deliberately
    // separate from `promo` so retiring the discount does not silently
    // remove the waitlist as well.
    waitlist: {
      label: 'Next cohort'
    },

    snapshot: {
      whoItsFor: [
        'Anyone who has tried ChatGPT, Copilot, Claude, or Gemini and found the answers underwhelming',
        'Office professionals asked to "use AI" at work without any training',
        'HR practitioners, executive assistants, and small business owners deciding whether a longer course is worth it',
        'Managers who need to judge whether their team is using AI sensibly',
        'Career changers and return-to-work professionals building current workplace skills'
      ],
      accreditation: 'Grounded in the Australian Privacy Principles under the Privacy Act 1988 and the Australian Government voluntary AI Safety Standard guardrails for responsible AI use.'
    },

    logistics: {
      classSize: 'Maximum 30 participants so every question gets answered live',
      requirements: [
        'Computer or tablet with a working microphone and internet access',
        'A free account on at least one general AI assistant (setup guidance sent on booking)',
        'One real, non-confidential work task you would like help with',
        'No technical, programming, or prior AI experience required',
        'Reliable internet connection (minimum 5 Mbps)'
      ]
    },

    overview: 'Most people who are disappointed by AI tools are not using a bad tool. They are writing a one-line prompt and hoping. This two-hour evening session teaches the small set of habits that consistently produce better output: giving the tool a role, supplying the context it cannot guess, stating the format and audience you need, showing an example of good, and correcting rather than restarting when the first answer misses. Everything is demonstrated live on ordinary Australian office work such as emails, summaries, drafts, and lists, using tools you already have access to. There is no coding, no API, and no jargon. We also cover what you must never paste into a general AI tool, including customer and employee personal information handled under the Privacy Act 1988, and how to sanitise a prompt so you can still get help with a sensitive task. The session closes by pointing you to the role-specific four-hour course that fits your work: HR, executive support, or running a small business. The listed price is 195 AUD including GST, and the September 2026 launch cohort is free with a promo code.',

    outcomes: [
      'Write a structured prompt containing a role, a task, the context, the constraints, and the output format you actually need',
      'Supply the background an AI tool cannot infer so the first answer is usable rather than generic',
      'Apply role setting and audience setting to change the tone and reading level of a draft on demand',
      'Iterate on a weak answer with targeted corrections instead of starting again from scratch',
      'Recognise information that must never be pasted into a general AI tool under the Australian Privacy Principles',
      'Sanitise a real work task so you can get useful AI help without exposing personal or commercial information',
      'Evaluate an AI answer for invented facts, wrong assumptions, and confident-sounding errors',
      'Choose the role-specific next course that matches your day-to-day work'
    ],

    audience: [
      'Office and administrative professionals in any industry',
      'HR practitioners, recruiters, and people and culture coordinators',
      'Executive assistants, personal assistants, and office managers',
      'Small business owners, sole traders, and tradespeople doing their own admin',
      'Team leaders and managers assessing AI use across their team',
      'Career changers and return-to-work professionals refreshing workplace skills'
    ],

    prerequisites: [
      'Comfortable using email, a web browser, and a word processor',
      'Access to at least one general AI assistant such as ChatGPT, Copilot, Claude, or Gemini',
      'No technical, programming, or data skills required',
      'No prior experience with AI tools required'
    ],

    modules: [
      {
        title: 'Anatomy of a Prompt That Works',
        duration: '45 minutes',
        topics: [
          'The five parts of a reliable prompt: role, task, context, constraints, and output format',
          'Why one-line prompts produce generic answers, demonstrated side by side',
          'Specifying length, tone, structure, and reading level so you can use the answer as-is',
          'Showing an example of good output instead of describing it',
          'Rewriting three real workplace prompts live and comparing the before and after'
        ]
      },
      {
        title: 'Context, Role Setting, and Getting to a Good Answer',
        duration: '45 minutes',
        topics: [
          'Giving the tool the background it cannot guess: audience, purpose, history, and constraints',
          'Role setting to shift voice, formality, and level of detail for different readers',
          'Correcting a near-miss answer with targeted feedback rather than restarting the conversation',
          'Keeping a long conversation on track and recognising when to start a fresh one',
          'Spotting invented facts, wrong assumptions, and confidently stated errors before you send anything'
        ]
      },
      {
        title: 'What Not to Share, and Where to Go Next',
        duration: '30 minutes',
        topics: [
          'Information that must never enter a general AI tool: customer and employee personal details, health information, bank details, credentials, and confidential commercial terms',
          'Australian Privacy Principles obligations in plain language for everyday tool use',
          'Sanitising a real task so you keep the help and lose the exposure',
          'Building a small personal prompt library you can reuse each week',
          'Choosing your next step: the four-hour AI Quick Start courses for HR, executive assistants, and small business owners'
        ]
      }
    ],

    format: {
      type: 'Live online evening session, Monday 6-8pm',
      includes: [
        '2 hours of live, interactive instruction with a real instructor',
        'Live prompt rewriting using participant examples',
        'A one-page prompt structure card you can keep beside your keyboard',
        'A starter prompt library of ready-to-use patterns for everyday office tasks',
        'A plain-English "never paste this" checklist covering Australian privacy obligations',
        'Certificate of completion',
        'Live Q&A throughout, with time reserved at the end'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Live online evening cohorts run on scheduled dates. Register your interest and we will confirm the next dates.'
    },

    // Real, dated sessions. The Enrol button is gated on there being a
    // future entry here — without one the page offers the waitlist instead,
    // because taking $195 for an unidentified session is not a supply
    // commitment under Australian Consumer Law. (#341)
    // Cohort opens the week of Monday 21 September 2026.
    //
    // NOTE ON OFFSETS: NSW daylight saving begins Sunday 4 October 2026, so
    // 6pm Sydney is +10:00 (AEST) in September and +11:00 (AEDT) from October.
    // Writing +10:00 throughout would advertise the October run an hour late.
    sessions: [
      { start: '2026-09-21T18:00:00+10:00', end: '2026-09-21T20:00:00+10:00', capacity: 30 },
      { start: '2026-09-28T18:00:00+10:00', end: '2026-09-28T20:00:00+10:00', capacity: 30 },
      { start: '2026-10-05T18:00:00+11:00', end: '2026-10-05T20:00:00+11:00', capacity: 30 }
    ],

    pathways: [],

    skillsTaught: [
      'Practical prompt writing',
      'Context and role setting for AI tools',
      'Iterating and correcting AI output',
      'Evaluating AI answers for accuracy',
      'Safe AI input under Australian privacy obligations'
    ],

    hasCapstone: false,

    nextCourses: [
      'ai-quick-start-hr',
      'ai-quick-start-executive-assistants',
      'ai-quick-start-small-business'
    ],

    faqs: [
      {
        question: 'Is this really free, or is there a catch?',
        answer: 'The course is listed and sold at 195 AUD including GST, which is the full amount payable. A tax invoice is issued automatically on enrolment. You book a real seat in a real live session.'
      },
      {
        question: 'Do I need any technical background?',
        answer: 'None at all. This is a plain-English session for people who use a computer at work, not for developers. There is no code, no API, and no configuration. If you can write an email, you can write a good prompt by the end of the two hours.'
      },
      {
        question: 'How is this different from your Master Course in Prompt Engineering?',
        answer: 'Completely different audiences. The 120-hour AI Architect masterclass is an advanced program for developers and AI specialists building production systems with prompt chaining, function calling, and API-level control. This two-hour session is for non-technical professionals who simply want better answers out of the AI tools already on their desk.'
      },
      {
        question: 'Which AI tool do you teach?',
        answer: 'We teach principles that transfer across every general assistant, and demonstrate on the widely used ones including ChatGPT, Microsoft Copilot, Claude, and Gemini. You will not be pushed towards a particular vendor. Bring whichever tool your workplace has approved, and if you have none, we will point you to a free option before the session.'
      },
      {
        question: 'What if my workplace has restrictions on AI use?',
        answer: 'That is common and sensible, and the session respects it. We cover what makes an organisation nervous, what is safe to put into a general tool, and how to sanitise a task so you stay inside your acceptable-use policy. Several participants use the session to work out what to ask their IT team for.'
      },
      {
        question: 'Two hours seems short. What can I realistically get out of it?',
        answer: 'The session is deliberately narrow. It covers prompt structure, context, iteration, and safe input, and does not attempt to cover automation, agents, or governance. Most participants leave able to rewrite their own everyday prompts and get noticeably better output the next morning. The four-hour role-specific courses go deeper for your particular job.'
      },
      {
        question: 'Do I need to bring anything to the session?',
        answer: 'Bring one real work task you would like AI help with, with any confidential details removed. We use participant examples for the live rewriting exercises, and people who bring something concrete get considerably more value than those who watch. A logged-in AI account before the session starts saves time.'
      },
      {
        question: 'What happens after this course?',
        answer: 'The final segment points you to the four-hour AI Quick Start course that matches your role: HR Teams on Tuesday evenings, Executive Assistants on Wednesday, or Small Business Owners on Thursday. Each is 395 AUD including GST and takes the same habits into the specific documents, decisions, and risks of that role. There is no obligation to continue.'
      }
    ]
};
